import { Component, OnInit, Injector, HostListener, AfterViewInit, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { scheduleStatus, Schedule } from 'app/core/interfaces/schedule';
import { scheduleFilter } from 'app/core/data/forms.json';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { tabI } from 'app/core/interfaces/layout';
import { Encounter, EncounterListResult } from 'app/core/interfaces/encounter';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { PractitionerScheduleDetailsComponent } from './details/schedule-details.component';
import { ScheduleEditStatusComponent as PractitionerScheduleEditStatusComponent } from './edit-status/schedule-edit-status.component';
import { PaymentEncounterComponent } from 'app/modules/patient/pages/schedule/status/payment-encounter/payment-encounter.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'page-schedule-status',
  templateUrl: './schedule-status.component.html',
  styleUrls: ['./schedule-status.component.scss'],
})
export class PractitionerScheduleStatusComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public title = 'Status de atendimento';
  public scheduleStatus: scheduleStatus[] = [
    { id: 'planned', text: 'Realização pendente', color: 'red', icon: 'error' },
    { id: 'finished', text: 'Confirmado', color: 'blue', icon: 'check_circle' },
    { id: 'in-progress', text: 'Em atendimento', color: 'orange', icon: 'pending' },
    { id: 'isByOrder', text: 'Ordem de chegada', color: 'gray', icon: 'error' },
    { id: 'cancelled', text: 'Cancelado', color: 'red', icon: 'error' },
  ];

  public tipoSchedule = 0;
  public buscarScheduleControl = new FormControl('');
  public filterTipos = scheduleFilter;
  public tabs: tabI[] = [];
  public activeTabId = 1;
  public encounters: Encounter[] = [];
  public schedules: Schedule[] = [];
  public practitionerLogged: any = {};

  // Autocomplete
  public autocompleteSuggestions: string[] = [];
  public showAutocompleteHint = false;
  public autocompleteLoading = false;

  // Paginação
  public total = 0;
  public lastEvaluateKey: any | null = null;
  public pageSize = 25;
  public hasMore = true;

  public loadingInitial = false;
  public loadingMore = false;
  public loadError = false;

  public appliedSearchTerm = '';
  public readonly minSearchLength = 2;

  private io?: IntersectionObserver;
  private sentinelChangesSub?: Subscription;
  private listSub?: Subscription;

  private currentScrollRoot: HTMLElement | Window = window;
  private userScrolledOnce = false;
  private onScrollHandler = () => {
    const top = this.getScrollTop(this.currentScrollRoot);
    if (top > 0) this.userScrolledOnce = true;
  };

  @ViewChildren('sentinel') sentinels!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private injector: Injector,
    private encounterService: EncounterService,
  ) {
    super(injector);
    this.initTabs();
  }

  ngOnInit(): void {
    const user = this.getUserLogged();
    this.loading = false;

    this.practitionerLogged = {
      practitioner_id: user.id,
      name: user.name,
      email: user.email,
    };

    this.setupSearch();

    // carga inicial
    this.applyFilters(true);
  }

  ngAfterViewInit(): void {
    this.sentinelChangesSub = this.sentinels.changes.subscribe(() => {
      setTimeout(() => this.setupInfiniteScroll(), 0);
    });

    setTimeout(() => this.setupInfiniteScroll(), 0);
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
    this.detachScrollListener();
    this.sentinelChangesSub?.unsubscribe();
    this.listSub?.unsubscribe();
  }

  private setupSearch() {
    this.buscarScheduleControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value: any) => {
      const raw = String(value ?? '');
      const term = raw.trim();

      this.showAutocompleteHint = term.length > 0 && term.length < this.minSearchLength;

      // Não chama API com 1 char (evita spam e melhora UX)
      if (term.length === 0) {
        this.applyFilters(true, '');
        return;
      }

      if (term.length < this.minSearchLength) {
        this.autocompleteSuggestions = [];
        return;
      }

      this.applyFilters(true, term);
    });
  }

  public onSearchSubmit() {
    const raw = String(this.buscarScheduleControl.value ?? '');
    const term = raw.trim();

    if (term.length === 0) {
      this.applyFilters(true, '');
      return;
    }

    if (term.length < this.minSearchLength) {
      this.showAutocompleteHint = true;
      return;
    }

    this.applyFilters(true, term.toLowerCase());
  }

  public clearSearch() {
    this.buscarScheduleControl.setValue('', { emitEvent: true });
    this.autocompleteSuggestions = [];
    this.showAutocompleteHint = false;
  }

  public onTipoChange() {
    this.resetLazyGate();
    this.refreshObserver();
  }

  private applyFilters(reset: boolean, termOverride?: string) {
    const raw = termOverride !== undefined ? termOverride : String(this.buscarScheduleControl.value ?? '').trim();

    this.appliedSearchTerm = raw.length >= this.minSearchLength ? raw.toLowerCase() : '';

    this.getEncounters(reset, this.appliedSearchTerm);
  }

  // ----------------------------
  // Paginação / API
  // ----------------------------

  private buildParams(loadMore: boolean, search: string): HttpParams {
    let params = new HttpParams().set('page-size', String(this.pageSize));

    if (search) params = params.set('search', search);

    if (loadMore && this.lastEvaluateKey) {
      const lek = typeof this.lastEvaluateKey === 'string' ? this.lastEvaluateKey : JSON.stringify(this.lastEvaluateKey);

      params = params.set('last-evaluated-key', lek);
    }

    return params;
  }

  public getEncounters(reset: boolean = true, search: string = '') {
    if (this.loading) return;

    this.listSub?.unsubscribe();

    if (reset) {
      this.encounters = [];
      this.total = 0;
      this.lastEvaluateKey = null;
      this.hasMore = true;
      this.loadError = false;

      this.resetLazyGate();
    }

    this.loading = true;
    this.loadingInitial = reset;
    this.loadingMore = !reset;

    this.autocompleteLoading = !!search && reset;

    const params = this.buildParams(!reset, search);

    this.listSub = this.encounterService.listByPractitionerNewPaged(this.practitionerLogged.practitioner_id, params).subscribe({
      next: (res: EncounterListResult) => {
        const items = res.items ?? [];

        this.encounters = [...this.encounters, ...items];

        this.total = res.count ?? this.total;
        this.lastEvaluateKey = res.lastEvaluatedKey ?? null;
        this.hasMore = !!this.lastEvaluateKey;

        if (search) this.updateSuggestionsFromItems(items, search);

        this.finishLoading();
        this.refreshObserver();
      },
      error: (err) => {
        console.log('error buscar encounter', err);
        this.loadError = true;
        this.finishLoading();
        this.refreshObserver();
      },
    });
  }

  private finishLoading() {
    this.loading = false;
    this.loadingInitial = false;
    this.loadingMore = false;
    this.autocompleteLoading = false;
  }

  public loadMore() {
    if (this.loading || !this.hasMore) return;

    this.userScrolledOnce = true;

    this.getEncounters(false, this.appliedSearchTerm);
  }

  public retry() {
    this.applyFilters(true);
  }

  public onTabChange(ev: MatTabChangeEvent) {
    this.activeTabId = this.tabs[ev.index]?.id ?? 1;
    this.refreshObserver();
  }

  // ----------------------------
  // Lazy loading: IntersectionObserver + gate
  // ----------------------------

  private resetLazyGate() {
    this.userScrolledOnce = false;
  }

  private getScrollTop(root: HTMLElement | Window): number {
    if (root instanceof Window) return window.scrollY || 0;
    return root.scrollTop || 0;
  }

  private getVisibleSentinel(): HTMLElement | null {
    if (!this.sentinels || this.sentinels.length === 0) return null;

    const candidates = this.sentinels.toArray().map((s) => s.nativeElement);
    const visible = candidates.find((el) => !!el && el.offsetParent !== null);
    return visible ?? candidates[0] ?? null;
  }

  private findScrollParent(el: HTMLElement | null): HTMLElement | null {
    if (!el) return null;

    let parent = el.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const canScroll = /(auto|scroll)/.test(overflowY);
      const hasScrollableContent = parent.scrollHeight > parent.clientHeight;

      if (canScroll && hasScrollableContent) return parent;

      parent = parent.parentElement;
    }

    return null;
  }

  private detachScrollListener() {
    if (this.currentScrollRoot instanceof Window) {
      window.removeEventListener('scroll', this.onScrollHandler);
    } else {
      this.currentScrollRoot.removeEventListener('scroll', this.onScrollHandler);
    }
  }

  private attachScrollListener(root: HTMLElement | null) {
    this.detachScrollListener();

    this.currentScrollRoot = root ?? window;

    if (this.currentScrollRoot instanceof Window) {
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    } else {
      this.currentScrollRoot.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }
  }

  private setupInfiniteScroll() {
    const sentinelEl = this.getVisibleSentinel();
    if (!sentinelEl) return;

    this.io?.disconnect();

    const scrollRoot = this.findScrollParent(sentinelEl);
    this.attachScrollListener(scrollRoot);

    this.io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        if (!this.userScrolledOnce && this.getScrollTop(this.currentScrollRoot) === 0) return;
        if (this.loading || !this.hasMore) return;

        this.loadMore();
      },
      {
        root: scrollRoot,
        threshold: 0.15,
        rootMargin: '0px 0px 180px 0px',
      },
    );

    this.io.observe(sentinelEl);
  }

  private refreshObserver() {
    setTimeout(() => this.setupInfiniteScroll(), 50);
  }

  // ----------------------------
  // Autocomplete (sugestões)
  // ----------------------------

  private updateSuggestionsFromItems(items: Encounter[], term: string) {
    const normalized = term.toLowerCase().trim();
    if (!normalized) {
      this.autocompleteSuggestions = [];
      return;
    }

    const suggestions = new Set<string>();

    items.forEach((encounter) => {
      if (encounter.patient?.name && encounter.patient.name.toLowerCase().includes(normalized)) {
        suggestions.add(encounter.patient.name);
      }
      if (encounter.practitioner?.name && encounter.practitioner.name.toLowerCase().includes(normalized)) {
        suggestions.add(encounter.practitioner.name);
      }
      if (encounter.organization?.name && encounter.organization.name.toLowerCase().includes(normalized)) {
        suggestions.add(encounter.organization.name);
      }
      if (encounter.name && encounter.name.toLowerCase().includes(normalized)) {
        suggestions.add(encounter.name);
      }

      const formattedDate = this.formatarData(encounter.startDate);
      if (formattedDate && formattedDate.includes(normalized)) suggestions.add(formattedDate);
    });

    this.autocompleteSuggestions = Array.from(suggestions).slice(0, 6);
  }

  // ----------------------------
  // Helpers / filtros locais
  // ----------------------------

  public get buscarSchedule(): string {
    return String(this.buscarScheduleControl.value ?? '');
  }

  public get scheduleFiltro(): Schedule[] {
    return [...this.schedules];
  }

  public get atendimentosFiltro(): Encounter[] {
    const tipo = this.tipoSchedule;
    return this.encounters.filter((item) => this.matchTipo(item, tipo));
  }

  public get atendimentosFinalizados(): Encounter[] {
    return this.atendimentosFiltro.filter((item) => item.status === 'cancelled' || item.status === 'finished');
  }

  public get atendimentosEmAberto(): Encounter[] {
    return this.atendimentosFiltro.filter((item) => item.status === 'planned' || item.status === 'in-progress');
  }

  private matchTipo(item: Encounter, tipo: number): boolean {
    if (tipo === 0) return true;
    if (tipo === 1) return item.type === 'Consulta';
    if (tipo === 2) return item.type === 'Exame';
    return false;
  }

  private formatarData(dateStr: string): string {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  public getContent(tabId: number): Encounter[] {
    return tabId === 1 ? this.atendimentosEmAberto : this.atendimentosFinalizados;
  }

  trackByEncounter(index: number, item: Encounter) {
    return item.encounter_id;
  }

  initTabs() {
    this.tabs = [
      { id: 1, title: 'Agendados', type: 'encounter' },
      { id: 2, title: 'Finalizados', type: 'encounter' },
    ];
  }

  // ----------------------------
  // Ações (mantidas)
  // ----------------------------

  openComponentDialogScheduleDetailForm(encounter: Encounter) {
    const data = {
      title: `Dados do atendimento - ${encounter.encounter_id}`,
      param: {
        encounterFHIR: this.selectEncounterFHIR(encounter),
        encounter: encounter,
      },
    };
    this.dialog.openComponentDialog(PractitionerScheduleDetailsComponent, data);
  }

  getStatusPaymentText(statusPayment?: string): string {
    switch (statusPayment) {
      case 'PAID':
        return 'PAGO';
      case 'NOT_PAID':
        return 'PENDENTE';
      default:
        return 'NÃO GERADO';
    }
  }

  editStatus(encounter: Encounter) {
    const data = {
      title: `Alterar status - ${encounter.encounter_id}`,
      param: {
        encounter: encounter,
      },
    };

    this.dialog.openSmallComponentDialog(PractitionerScheduleEditStatusComponent, data).subscribe({
      next: (updatedEncounter?: Encounter) => {
        if (updatedEncounter) {
          this.replaceEncounter(updatedEncounter);
        }
      },
    });
  }

  private replaceEncounter(updatedEncounter: Encounter) {
    this.encounters = this.encounters.map((encounter) => (encounter.encounter_id === updatedEncounter.encounter_id ? updatedEncounter : encounter));
    this.refreshObserver();
  }

  getTextStatus(statusCode: any, isByOrder?: boolean) {
    const adjustedStatusCode = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    return this.scheduleStatus.find((item) => item.id === adjustedStatusCode)?.text;
  }

  getColorStatus(statusCode: any, isByOrder?: boolean, statusPayment?: string) {
    const adjustedStatusCode = isByOrder && statusCode === 'planned' ? 'isByOrder' : statusCode;
    if (adjustedStatusCode === 'planned' && statusPayment === 'PAID') return 'green';
    if (adjustedStatusCode === 'finished') return 'blue';
    return this.scheduleStatus.find((item) => item.id === adjustedStatusCode)?.color;
  }

  openPaymentEncounter(encounter: any) {
    const data = {
      title: 'Pagamento Encounter',
      param: {
        encounter: encounter,
      },
    };

    this.dialog.openComponentDialog(PaymentEncounterComponent, data).subscribe({
      next: () => {
        this.applyFilters(true);
      },
    });
  }

  selectEncounterFHIR(encounterSelected: Encounter) {
    return this.encounters.find((e) => e.encounter_id === encounterSelected.encounter_id);
  }

  isEncounterApprovedToPaid(encounter: any) {
    const value_agreement = parseFloat(encounter?.value_agreement ?? '0');
    if (encounter.scheduling_method == 'PARTICULAR') return true;
    return encounter.agreement_approved && value_agreement > 0;
  }

  @HostListener('window:keydown.enter', ['$event'])
  onEnterKey(ev: KeyboardEvent) {
    // Evita “Enter” em lugares aleatórios
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    if (target.tagName?.toLowerCase() !== 'input') return;

    this.onSearchSubmit();
  }

  public get searchValueSafe(): string {
    return String(this.buscarScheduleControl.value ?? '');
  }

  public get hasMinSearch(): boolean {
    return this.searchValueSafe.trim().length >= this.minSearchLength;
  }

  public onSuggestionSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value as string;
    this.buscarScheduleControl.setValue(value, { emitEvent: false });
    this.onSearchSubmit();
  }
}
